import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.neevtechnologies.in/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem("token");
      // Only redirect if not already on login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username: string, password: string) =>
    api.post("/auth/login", { username, password }),
  getMe: () => api.get("/auth/me"),
};

// Company Profile API
export const profileAPI = {
  get: () => api.get("/profile"),
  create: (data: any) => api.post("/profile", data),
  update: (data: any) => api.put("/profile", data),
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append("logo", file);
    return api.post("/profile/upload-logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadSignature: (file: File) => {
    const formData = new FormData();
    formData.append("signature", file);
    return api.post("/profile/upload-signature", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getNextInvoiceNumber: () => api.get("/profile/next-invoice-number"),
};

// Clients API
export const clientsAPI = {
  getAll: (params?: any) => api.get("/clients", { params }),
  getById: (id: string) => api.get(`/clients/${id}`),
  create: (data: any) => api.post("/clients", data),
  update: (id: string, data: any) => api.put(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
  search: (query: string) => api.get(`/clients/search/${query}`),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params?: any) => api.get("/invoices", { params }),
  getById: (id: string) => api.get(`/invoices/${id}`),
  create: (data: any) => api.post("/invoices", data),
  update: (id: string, data: any) => api.put(`/invoices/${id}`, data),
  delete: (id: string) => api.delete(`/invoices/${id}`),
  sendEmail: (id: string) => api.post(`/invoices/${id}/send-email`),
  updateStatus: (id: string, status: string) =>
    api.patch(`/invoices/${id}/status`, { status }),
  getAnalytics: (params?: any) =>
    api.get("/invoices/analytics/summary", { params }),
};

// Receipts API
export const receiptsAPI = {
  getAll: (params?: any) => api.get("/receipts", { params }),
  getById: (id: string) => api.get(`/receipts/${id}`),
  create: (data: any) => api.post("/receipts", data),
  update: (id: string, data: any) => api.put(`/receipts/${id}`, data),
  delete: (id: string) => api.delete(`/receipts/${id}`),
  sendEmail: (id: string) => api.post(`/receipts/${id}/send-email`),
  generateFromInvoice: (invoiceId: string, data?: any) =>
    api.post(`/receipts/generate-from-invoice/${invoiceId}`, data),
};

// Services/Goods API
export const servicesAPI = {
  getAll: (params?: any) => api.get("/services", { params }),
  getById: (id: string) => api.get(`/services/${id}`),
  create: (data: any) => api.post("/services", data),
  update: (id: string, data: any) => api.put(`/services/${id}`, data),
  delete: (id: string) => api.delete(`/services/${id}`),
  search: (query: string) =>
    api.get("/services", { params: { search: query } }),
};

// Expenses API
export interface Expense {
  _id: string;
  billNumber: string;
  billDate: string;
  vendorName: string;
  vendorGstin?: string;
  description?: string;
  category?: string;
  taxableAmount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  tdsDeducted: number;
  paymentMode: string;
  notes?: string;
  totalTaxAmount: number;
  billFileUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

// Editable fields sent to the API (file handled separately)
export interface ExpenseInput {
  billNumber: string;
  billDate: string;
  vendorName: string;
  vendorGstin?: string;
  description?: string;
  category?: string;
  taxableAmount?: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  totalAmount: number;
  tdsDeducted?: number;
  paymentMode?: string;
  notes?: string;
}

const toExpenseFormData = (data: Partial<ExpenseInput>, file?: File | null) => {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) fd.append(key, String(value));
  });
  if (file) fd.append('billFile', file);
  return fd;
};

export const expensesAPI = {
  getAll: () => api.get<Expense[]>('/expenses'),
  getById: (id: string) => api.get<Expense>(`/expenses/${id}`),
  create: (data: ExpenseInput, file?: File | null) =>
    api.post<Expense>('/expenses', toExpenseFormData(data, file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id: string, data: Partial<ExpenseInput>, file?: File | null) =>
    api.put<Expense>(`/expenses/${id}`, toExpenseFormData(data, file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id: string) => api.delete(`/expenses/${id}`),
};

// Accounts / Earnings API
export interface EarningsRow {
  invoiceId: string | null;
  invoiceNumber: string;
  invoiceDate: string | null;
  receiptId: string | null;
  receiptNumber: string | null;
  receiptDate: string | null;
  clientName: string;
  clientGstin: string;
  taxableAmount: number;
  taxAmount: number;
  tdsDeducted: number;
  totalAmount: number;
  invoiceShareToken: string | null;
  receiptShareToken: string | null;
}

export interface EarningsSummary {
  totalInvoices: number;
  totalReceipts: number;
  pendingReceipts: number;
  totalTaxable: number;
  totalTax: number;
  totalTds: number;
  totalAmount: number;
  totalReceived: number;
}

export interface EarningsResponse {
  rows: EarningsRow[];
  summary: EarningsSummary;
  fy: string;
  month: number | null;
}

export interface ExpenseReportRow {
  expenseId: string;
  billNumber: string;
  billDate: string;
  vendorName: string;
  vendorGstin: string;
  description: string;
  category: string;
  taxableAmount: number;
  taxAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  tdsDeducted: number;
  totalAmount: number;
  paymentMode: string;
  billFileUrl: string | null;
}

export interface ExpenseReportSummary {
  totalExpenses: number;
  totalTaxable: number;
  totalTax: number;
  totalTds: number;
  totalAmount: number;
}

export interface ExpenseReportResponse {
  rows: ExpenseReportRow[];
  summary: ExpenseReportSummary;
  fy: string;
  month: number | null;
}

export const accountsAPI = {
  getEarnings: (fy: string, month?: number | null) =>
    api.get<EarningsResponse>('/accounts/earnings', {
      params: { fy, ...(month ? { month } : {}) },
    }),
  getExpenses: (fy: string, month?: number | null) =>
    api.get<ExpenseReportResponse>('/accounts/expenses', {
      params: { fy, ...(month ? { month } : {}) },
    }),
};

// Bare instance (no auth header, no 401 redirect) for public share pages
const publicClient = axios.create({ baseURL: API_BASE_URL });

export const publicAPI = {
  getInvoice: (token: string) => publicClient.get(`/public/invoice/${token}`),
  getReceipt: (token: string) => publicClient.get(`/public/receipt/${token}`),
};

export default api;
